import dayjs, { extend } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import useLocalStorage, { STORAGE_KEY } from '@hooks/useLocalStorage';
import {
  CURRENT_TERMS_VERSION_MAP,
  TermsType,
  NO_VERSION_YET,
} from '@pages/UserGuide/Detail/constants/general';
import { useAuthorizationContext } from '@src/context/AuthorizationContext';
import { isValidDateStr } from '@src/utils/utils';

import { getTermsTypeToProcess } from './utils';

extend(isSameOrAfter);

type HasNewTermsMap = Record<TermsType, boolean>;

interface TermsUpdateNotificationType {
  hasNewTerms: boolean;
  handleSetNewTerms: (bool: boolean) => void;

  handleMarkAsRead: (type: TermsType) => void;
  handleMarkAsReadAll: () => void;

  checkHasNewTermsMap: () => HasNewTermsMap;
}

export const TermsUpdateNotification =
  createContext<TermsUpdateNotificationType | null>(null);

const TermsUpdateNotificationProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { getStorage, setStorage } = useLocalStorage();
  const { marketing_id, rolesProduct } = useAuthorizationContext();

  const typeToProcessMap = getTermsTypeToProcess(rolesProduct);

  const [hasNewTerms, setHasNewTerms] = useState(false);
  const handleSetNewTerms = (bool: boolean) => setHasNewTerms(bool);

  const checkHasNewTermsMap = useCallback(() => {
    return typeToProcessMap.reduce<HasNewTermsMap>((accumulated, type) => {
      const lastTermsReadMap =
        getStorage(STORAGE_KEY.LAST_TERMS_READ(type)) ?? {};

      const lastTermsRead = lastTermsReadMap?.[marketing_id];

      const isDateLastTermsRead = isValidDateStr(lastTermsRead);

      const todayIsSameOrAfterCurrentVersion = dayjs().isSameOrAfter(
        dayjs(CURRENT_TERMS_VERSION_MAP[type]),
        'day',
      );
      const currentVersionIsSameOrAfterLastRead =
        !isDateLastTermsRead ||
        !lastTermsRead ||
        dayjs(CURRENT_TERMS_VERSION_MAP[type]).isSameOrAfter(
          dayjs(lastTermsRead),
          'day',
        );

      accumulated[type] =
        CURRENT_TERMS_VERSION_MAP[type] !== NO_VERSION_YET &&
        CURRENT_TERMS_VERSION_MAP[type] !== lastTermsRead &&
        currentVersionIsSameOrAfterLastRead &&
        todayIsSameOrAfterCurrentVersion;

      return accumulated;
    }, {} as HasNewTermsMap);
  }, [getStorage, marketing_id, typeToProcessMap]);

  const handleMarkAsRead = useCallback(
    (type: TermsType) => {
      const previousTermsReadMap = getStorage(
        STORAGE_KEY.LAST_TERMS_READ(type),
      );

      setStorage(STORAGE_KEY.LAST_TERMS_READ(type), {
        ...previousTermsReadMap,
        [marketing_id]: CURRENT_TERMS_VERSION_MAP[type],
      });
    },
    [getStorage, marketing_id, setStorage],
  );

  const handleMarkAsReadAll = useCallback(() => {
    const typeToMarkAsRead = Object.entries(checkHasNewTermsMap()) as [
      TermsType,
      boolean,
    ][];

    typeToMarkAsRead.forEach(([type, isNew]) => {
      if (!isNew) return;

      handleMarkAsRead(type);
    });
  }, [checkHasNewTermsMap, handleMarkAsRead]);

  const value = useMemo(
    () => ({
      hasNewTerms,
      handleSetNewTerms,

      handleMarkAsRead,
      handleMarkAsReadAll,

      checkHasNewTermsMap,
    }),
    [checkHasNewTermsMap, handleMarkAsRead, handleMarkAsReadAll, hasNewTerms],
  );

  return (
    <TermsUpdateNotification.Provider value={value}>
      {children}
    </TermsUpdateNotification.Provider>
  );
};

const useTermsUpdateNotification = () => {
  const context = useContext(TermsUpdateNotification);

  if (!context) {
    throw new Error(
      'useTermsUpdateNotification was used outside of its Provider',
    );
  }

  return context;
};

export { TermsUpdateNotificationProvider, useTermsUpdateNotification };
